import { t } from '../i18n.js';
import { twoFactorSetup, twoFactorEnable, twoFactorVerify, completeLoginAfterTwoFactor } from '../services/auth.js';

/**
 * Creates the 2FA setup screen (first-time enrollment).
 *
 * Steps: Scan QR -> Confirm with TOTP code -> Save recovery codes -> Final verify
 *
 * @param {string} tempToken  Temporary JWT from login response
 * @param {string} instance   CRM instance name
 * @param {function} onComplete  Called after successful 2FA verification (login done)
 * @param {function} onCancel    Called when user cancels back to login
 * @returns {HTMLElement}
 */
export function createTwoFactorSetupScreen(tempToken, instance, onComplete, onCancel) {
  const container = document.createElement('div');
  container.className = 'tfa-container';

  let currentStep = 0;
  let qrCode = '';
  let secret = '';
  let recoveryCodes = [];

  const steps = [
    t('twoFactorStepScan'),
    t('twoFactorStepConfirm'),
    t('twoFactorStepSaveCodes'),
    t('twoFactorStepVerify'),
  ];

  function renderStepper() {
    return `
      <div class="tfa-stepper">
        ${steps.map((label, i) => `
          <div class="tfa-step ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'completed' : ''}">
            <div class="tfa-step-number">${i < currentStep ? '✓' : i + 1}</div>
            <div class="tfa-step-label">${label}</div>
          </div>
          ${i < steps.length - 1 ? '<div class="tfa-step-line"></div>' : ''}
        `).join('')}
      </div>
    `;
  }

  function renderStep0_Scan() {
    return `
      <div class="tfa-step-content">
        <p class="tfa-description">${t('twoFactorScanQrCode')}</p>
        ${qrCode ? `<div class="tfa-qr-wrapper"><img src="${qrCode}" alt="QR Code" class="tfa-qr-image" /></div>` : '<div class="tfa-loading"><div class="loading-spinner"></div></div>'}
        ${secret ? `
          <div class="tfa-manual-entry">
            <p class="tfa-manual-label">${t('twoFactorManualEntry')}</p>
            <div class="tfa-secret-box">
              <code class="tfa-secret-code">${secret}</code>
              <button type="button" class="tfa-copy-btn" data-action="copy-secret" title="Copy">📋</button>
            </div>
          </div>
        ` : ''}
        <button type="button" class="tfa-primary-btn" data-action="scanned" ${!qrCode ? 'disabled' : ''}>${t('twoFactorScannedQr')}</button>
      </div>
    `;
  }

  function renderStep1_Confirm() {
    return `
      <div class="tfa-step-content">
        <p class="tfa-description">${t('twoFactorEnterCodeConfirm')}</p>
        <div class="tfa-code-input-group">
          <input type="text" class="tfa-code-input" maxlength="6" pattern="[0-9]{6}" placeholder="000000" autocomplete="one-time-code" inputmode="numeric" />
        </div>
        <div class="tfa-error" style="display:none;"></div>
        <button type="button" class="tfa-primary-btn" data-action="enable">${t('twoFactorVerifyAndEnable')}</button>
      </div>
    `;
  }

  function renderStep2_RecoveryCodes() {
    return `
      <div class="tfa-step-content">
        <div class="tfa-success-banner">${t('twoFactorEnabledSuccess')}</div>
        <h3 class="tfa-codes-title">${t('twoFactorSaveRecoveryCodes')}</h3>
        <p class="tfa-codes-desc">${t('twoFactorRecoveryCodesDesc')}</p>
        <div class="tfa-codes-grid">
          ${recoveryCodes.map(code => `<div class="tfa-code-item"><code>${code}</code></div>`).join('')}
        </div>
        <button type="button" class="tfa-secondary-btn" data-action="copy-codes">📋 ${t('twoFactorCopyAllCodes')}</button>
        <button type="button" class="tfa-primary-btn" data-action="codes-saved">${t('twoFactorCodesSaved')}</button>
      </div>
    `;
  }

  function renderStep3_FinalVerify() {
    return `
      <div class="tfa-step-content">
        <p class="tfa-description">${t('twoFactorEnterNewCode')}</p>
        <div class="tfa-code-input-group">
          <input type="text" class="tfa-code-input" maxlength="6" pattern="[0-9]{6}" placeholder="000000" autocomplete="one-time-code" inputmode="numeric" />
        </div>
        <div class="tfa-error" style="display:none;"></div>
        <button type="button" class="tfa-primary-btn" data-action="complete">${t('twoFactorCompleteSetup')}</button>
      </div>
    `;
  }

  function render() {
    const stepRenderers = [renderStep0_Scan, renderStep1_Confirm, renderStep2_RecoveryCodes, renderStep3_FinalVerify];
    container.innerHTML = `
      <div class="tfa-card">
        <h2 class="tfa-title">${t('twoFactorSetupTitle')}</h2>
        ${renderStepper()}
        ${stepRenderers[currentStep]()}
        <button type="button" class="tfa-back-link" data-action="back-to-login">${t('twoFactorBackToLogin')}</button>
      </div>
    `;
    attachEvents();
  }

  function attachEvents() {
    container.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', handleAction);
    });

    const codeInput = container.querySelector('.tfa-code-input');
    if (codeInput) {
      codeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
      });
      codeInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const submitBtn = container.querySelector('.tfa-primary-btn');
          if (submitBtn && !submitBtn.disabled) submitBtn.click();
        }
      });
      setTimeout(() => codeInput.focus(), 100);
    }
  }

  function clearError() {
    const errEl = container.querySelector('.tfa-error');
    if (errEl) errEl.style.display = 'none';
  }

  function showError(msg) {
    const errEl = container.querySelector('.tfa-error');
    if (errEl) {
      errEl.textContent = msg;
      errEl.style.display = 'block';
    }
  }

  function setLoading(btn, loading) {
    if (btn) {
      btn.disabled = loading;
      if (loading) btn.dataset.origText = btn.textContent;
      btn.textContent = loading ? '...' : (btn.dataset.origText || btn.textContent);
    }
  }

  async function handleAction(e) {
    const action = e.currentTarget.dataset.action;
    const btn = e.currentTarget;

    if (action === 'back-to-login') {
      onCancel();
      return;
    }

    if (action === 'copy-secret') {
      try {
        await navigator.clipboard.writeText(secret);
        btn.textContent = '✓';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      } catch (_) {}
      return;
    }

    if (action === 'copy-codes') {
      try {
        await navigator.clipboard.writeText(recoveryCodes.join('\n'));
        btn.textContent = `✓ ${t('twoFactorCodesCopied')}`;
        setTimeout(() => { btn.textContent = `📋 ${t('twoFactorCopyAllCodes')}`; }, 2000);
      } catch (_) {}
      return;
    }

    if (action === 'scanned') {
      currentStep = 1;
      render();
      return;
    }

    if (action === 'enable') {
      clearError();
      const code = container.querySelector('.tfa-code-input')?.value;
      if (!code || code.length !== 6) {
        showError(t('twoFactorEnterSixDigit'));
        return;
      }
      setLoading(btn, true);
      try {
        const result = await twoFactorEnable(tempToken, instance, code);
        recoveryCodes = result.recoveryCodes || [];
        currentStep = 2;
        render();
      } catch (err) {
        showError(err.message || t('twoFactorInvalidCode'));
        setLoading(btn, false);
      }
      return;
    }

    if (action === 'codes-saved') {
      currentStep = 3;
      render();
      return;
    }

    if (action === 'complete') {
      clearError();
      const code = container.querySelector('.tfa-code-input')?.value;
      if (!code || code.length !== 6) {
        showError(t('twoFactorEnterSixDigit'));
        return;
      }
      setLoading(btn, true);
      try {
        const userData = await twoFactorVerify(tempToken, instance, { code });
        completeLoginAfterTwoFactor(userData, instance);
        onComplete();
      } catch (err) {
        showError(err.message || t('twoFactorInvalidCode'));
        setLoading(btn, false);
      }
    }
  }

  async function init() {
    render();
    try {
      const setupData = await twoFactorSetup(tempToken, instance);
      qrCode = setupData.qrCode;
      secret = setupData.secret;
      render();
    } catch (err) {
      container.querySelector('.tfa-step-content').innerHTML = `
        <div class="tfa-error-banner">${t('twoFactorSetupFailed')}</div>
        <button type="button" class="tfa-back-link" data-action="back-to-login">${t('twoFactorBackToLogin')}</button>
      `;
      attachEvents();
    }
  }

  init();
  return container;
}
